import { z } from "zod";

export const RequestExampleDefaultImageAlt = "Concierge request example";

export const RequestExampleCta = z.object({
    label: z.string().min(1),
});
export type RequestExampleCta = z.infer<typeof RequestExampleCta>;

export const RequestExampleSlide = z.object({
    id: z.string().min(1),
    imageUrl: z.url(),
    imageAlt: z.string().min(1).default(RequestExampleDefaultImageAlt),
    category: z.string().min(1),
    description: z.string().min(1),
    cta: RequestExampleCta,
});
export type RequestExampleSlide = z.infer<typeof RequestExampleSlide>;

export const RequestExamplesContent = z.array(RequestExampleSlide).min(1);
export type RequestExamplesContent = z.infer<typeof RequestExamplesContent>;

export const RequestExamplesVariant = z.enum(["mobile", "desktop"]);
export type RequestExamplesVariant = z.infer<typeof RequestExamplesVariant>;

export const RequestExamplesSearchParams = z.object({
    tenant: z.string().min(1),
    locale: z.string().min(1),
    variant: RequestExamplesVariant.default("mobile"),
    parentOrigin: z.url().optional(),
});
export type RequestExamplesSearchParams = z.infer<typeof RequestExamplesSearchParams>;

export const RequestExamplesMessageType = z.enum([
    "requestExamples.ready",
    "requestExamples.close",
    "requestExamples.error",
    "requestExamples.sheetDrag",
    "requestExamples.visibility",
    "requestExamples.reset",
    "requestExamples.auth.token.request",
    "requestExamples.auth.token.response",
    "requestExamples.auth.token.error",
    "requestExamples.auth.unauthorized",
    "make_a_request",
]);
export type RequestExamplesMessageType = z.infer<typeof RequestExamplesMessageType>;

export const RequestExamplesReadyMessage = z.object({
    type: z.literal("requestExamples.ready"),
});
export type RequestExamplesReadyMessage = z.infer<typeof RequestExamplesReadyMessage>;

export const RequestExamplesCloseMessage = z.object({
    type: z.literal("requestExamples.close"),
    reason: z.enum(["closeButton", "completed", "error", "swipeUp", "swipeDown"]),
});
export type RequestExamplesCloseMessage = z.infer<typeof RequestExamplesCloseMessage>;

export const RequestExamplesSheetDragDirection = z.enum(["up", "down"]);
export type RequestExamplesSheetDragDirection = z.infer<
    typeof RequestExamplesSheetDragDirection
>;

export const RequestExamplesSheetDragMessage = z.object({
    type: z.literal("requestExamples.sheetDrag"),
    direction: RequestExamplesSheetDragDirection,
    offsetY: z.number(),
    phase: z.enum(["start", "move", "end"]),
    shouldDismiss: z.boolean().optional(),
    velocity: z.number().optional(),
});
export type RequestExamplesSheetDragMessage = z.infer<
    typeof RequestExamplesSheetDragMessage
>;

export const RequestExamplesCtaMessage = z.object({
    type: z.literal("make_a_request"),
    title: z.string().min(1).optional(),
    image: z.string().min(1).optional(),
    text: z.string().min(1).optional(),
});
export type RequestExamplesCtaMessage = z.infer<typeof RequestExamplesCtaMessage>;

export const RequestExamplesErrorMessage = z.object({
    type: z.literal("requestExamples.error"),
    code: z.enum(["contentUnavailable", "authUnavailable", "unexpected"]),
    message: z.string().min(1).optional(),
});
export type RequestExamplesErrorMessage = z.infer<typeof RequestExamplesErrorMessage>;

export const RequestExamplesVisibilityMessage = z.object({
    type: z.literal("requestExamples.visibility"),
    visible: z.boolean(),
});
export type RequestExamplesVisibilityMessage = z.infer<
    typeof RequestExamplesVisibilityMessage
>;

export const RequestExamplesResetMessage = z.object({
    type: z.literal("requestExamples.reset"),
});
export type RequestExamplesResetMessage = z.infer<typeof RequestExamplesResetMessage>;

export const RequestExamplesAuthTokenRequestMessage = z.object({
    type: z.literal("requestExamples.auth.token.request"),
    requestId: z.string().min(1),
    forceRefresh: z.boolean().optional(),
});
export type RequestExamplesAuthTokenRequestMessage = z.infer<
    typeof RequestExamplesAuthTokenRequestMessage
>;

export const RequestExamplesAuthTokenResponseMessage = z.object({
    type: z.literal("requestExamples.auth.token.response"),
    requestId: z.string().min(1),
    accessToken: z.string().min(1),
    expiresAt: z.iso.datetime({ offset: true }).optional(),
});
export type RequestExamplesAuthTokenResponseMessage = z.infer<
    typeof RequestExamplesAuthTokenResponseMessage
>;

export const RequestExamplesAuthTokenErrorMessage = z.object({
    type: z.literal("requestExamples.auth.token.error"),
    requestId: z.string().min(1),
    code: z.enum(["unavailable", "expired", "originRejected", "unexpected"]),
    message: z.string().min(1).optional(),
});
export type RequestExamplesAuthTokenErrorMessage = z.infer<
    typeof RequestExamplesAuthTokenErrorMessage
>;

export const RequestExamplesAuthUnauthorizedMessage = z.object({
    type: z.literal("requestExamples.auth.unauthorized"),
});
export type RequestExamplesAuthUnauthorizedMessage = z.infer<
    typeof RequestExamplesAuthUnauthorizedMessage
>;

export const RequestExamplesMessage = z.discriminatedUnion("type", [
    RequestExamplesReadyMessage,
    RequestExamplesCloseMessage,
    RequestExamplesCtaMessage,
    RequestExamplesErrorMessage,
    RequestExamplesSheetDragMessage,
    RequestExamplesAuthTokenRequestMessage,
    RequestExamplesAuthTokenResponseMessage,
    RequestExamplesAuthTokenErrorMessage,
    RequestExamplesAuthUnauthorizedMessage,
]);
export type RequestExamplesMessage = z.infer<typeof RequestExamplesMessage>;

export const RequestExamplesAuthTokenResultMessage = z.discriminatedUnion("type", [
    RequestExamplesAuthTokenResponseMessage,
    RequestExamplesAuthTokenErrorMessage,
]);
export type RequestExamplesAuthTokenResultMessage = z.infer<
    typeof RequestExamplesAuthTokenResultMessage
>;

export function parseRequestExamplesMessageData(
    data: unknown,
): RequestExamplesMessage | null {
    const parsed = RequestExamplesMessage.safeParse(parsePostMessageData(data));
    return parsed.success ? parsed.data : null;
}

export function parseRequestExamplesAuthTokenResultMessageData(
    data: unknown,
): RequestExamplesAuthTokenResultMessage | null {
    const parsed = RequestExamplesAuthTokenResultMessage.safeParse(
        parsePostMessageData(data),
    );
    return parsed.success ? parsed.data : null;
}

export const RequestExamplesParentControlMessage = z.discriminatedUnion("type", [
    RequestExamplesVisibilityMessage,
    RequestExamplesResetMessage,
]);
export type RequestExamplesParentControlMessage = z.infer<
    typeof RequestExamplesParentControlMessage
>;

export function parseRequestExamplesParentControlMessageData(
    data: unknown,
): RequestExamplesParentControlMessage | null {
    const parsed = RequestExamplesParentControlMessage.safeParse(
        parsePostMessageData(data),
    );
    return parsed.success ? parsed.data : null;
}

function parsePostMessageData(data: unknown): unknown {
    if (typeof data !== "string") {
        return data;
    }

    try {
        return JSON.parse(data);
    } catch {
        return null;
    }
}
